// ─────────────────────────────────────────────────────────────────────
// Shared time/court helpers.
//
// These are the *only* pieces of the engine the court resolver needs, and both
// are pure two-liners with no dependencies of their own. They live here so the
// resolver can run on the server without dragging in the whole tournament
// engine — and, crucially, without a second copy of them existing.
//
// apps/web/src/engine re-exports these, so every existing import keeps working
// and there is still exactly ONE definition. CLAUDE.md: never fork a parallel
// engine.
// ─────────────────────────────────────────────────────────────────────

/** "HH:MM" (or a bare hour number) → minutes since midnight. */
export function timeToMinutes(timeStr: string | number): number {
  if (typeof timeStr === 'number') return timeStr * 60; // a bare hour
  const [h, m] = String(timeStr).split(':').map(Number);
  return h * 60 + (m || 0);
}

/** The court a series match is played on, wherever it was recorded. */
export function serieMatchCourt(m: {
  court?: unknown;
  bookingInfo?: { court?: unknown } | null;
}): unknown {
  return m?.court != null ? m.court : (m?.bookingInfo?.court ?? null);
}

/** "HH:MM" → minutes since midnight. Tolerant of missing/!string input. */
export function toMinHHMM(t: string | null | undefined): number {
  if (!t || typeof t !== 'string') return 0;
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** The date a series match is played on, wherever it was recorded. */
export function serieMatchDate(m: {
  date?: unknown;
  bookingInfo?: { date?: unknown } | null;
}): unknown {
  return m?.date || m?.bookingInfo?.date || null;
}

/**
 * Minutes since midnight back to `HH:MM`.
 *
 * Moved here from apps/web/src/lib/date.ts with the engine closure (#183): the
 * scheduling code that uses it now runs on the server too. `lib/date` re-exports
 * both directions so browser call-sites are unchanged — and so there is ONE
 * implementation rather than the two that had already drifted apart into
 * separate files with identical bodies.
 */
export function fromMinHHMM(min: number): string {
  // Wrap past midnight: minutes >= 1440 (next day) render as 00:00–23:59.
  const wrapped = ((min % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
